import React, { useState, useEffect } from 'react';
import { X, Phone, Trash2, User } from 'lucide-react';
import dialerService from '../services/dialerService';

const AfterCallWorkModalV2 = ({
  isOpen,
  onClose,
  onSave,
  activeCall,
  upcomingCalls = []
}) => {
  const [outcomes, setOutcomes] = useState([]);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [remarks, setRemarks] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOutcomes();
    }
  }, [isOpen]);

  const fetchOutcomes = async () => {
    try {
      const response = await dialerService.getCallLogMaster();
      let data = response;

      // Handle ASP.NET AJAX WCF response pattern where data is wrapped in .d
      if (response && response.d) {
        data = response.d;
      }

      if (Array.isArray(data)) {
        setOutcomes(data);
      } else if (data && data.Details) {
        setOutcomes(data.Details || []);
      }
    } catch (error) {
      console.error('Failed to fetch outcomes:', error);
    }
  };

  if (!isOpen) return null;

  const handleSave = async (isExit = false) => {
    const payload = {
      scheduleCallId: activeCall?.scheduleCallId || 0,
      uniqueId: activeCall?.uniqueId || 0,
      outcomeId: parseInt(selectedOutcome),
      remarks: remarks
    };

    setIsLoading(true);
    try {
      await onSave(payload, isExit);
      onClose();
    } catch (error) {
      console.error('Save ACW error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h2 className="text-xl font-bold text-slate-800">After Call Work</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Outcome Select */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Outcome</label>
            <select
              value={selectedOutcome}
              onChange={(e) => setSelectedOutcome(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-slate-700"
            >
              <option value="">Select Outcome</option>
              {outcomes.map((outcome) => (
                <option key={outcome.OutcomeId} value={outcome.OutcomeId}>
                  {outcome.OutcomeName}
                </option>
              ))}
            </select>
          </div>

          {/* Remarks Textarea */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter Remarks"
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-slate-700 min-h-[100px] resize-none"
            />
          </div>

          {/* Upcoming Calls List */}
          <div className="bg-slate-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-700/50">
              <h3 className="text-sm font-bold text-white/90">List of upcoming call</h3>
            </div>
            <div className="divide-y divide-slate-700/30">
              {upcomingCalls.slice(0, 3).map((call, index) => (
                <div key={call.Id || index} className="px-5 py-4 flex items-center justify-between group hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center border border-slate-600">
                      <User size={20} className="text-white/60" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white tracking-tight">Entity: enquiry</p>
                      <p className="text-xs text-white/50 font-medium">{call.ScheduleMobileNo || call.MobileNo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-slate-900/50 text-white/80 text-[10px] font-bold rounded-md border border-slate-700 uppercase tracking-wider">
                      Upcoming Call
                    </span>
                    <button className="p-2 text-white/40 hover:text-rose-400 transition-colors">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
              {upcomingCalls.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-white/30 italic">No upcoming calls</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50/50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button 
            onClick={() => handleSave(false)}
            disabled={isLoading || !selectedOutcome || upcomingCalls.length < 2}
            className="px-6 py-2.5 text-sm font-bold bg-[#22b1eb] hover:bg-[#1da1d6] text-white rounded-xl shadow-lg shadow-sky-500/20 transition-all disabled:opacity-50 disabled:shadow-none"
          >
            {isLoading ? 'Saving...' : 'Save & Next'}
          </button>
          <button 
            onClick={() => handleSave(true)}
            disabled={isLoading || !selectedOutcome}
            className="px-6 py-2.5 text-sm font-bold bg-[#3b71ed] hover:bg-[#3465d6] text-white rounded-xl shadow-lg shadow-blue-600/20 transition-all disabled:opacity-50 disabled:shadow-none"
          >
            {isLoading ? 'Saving...' : 'Save Exit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AfterCallWorkModalV2;
